import { useState } from "react";
import { Menu, X } from "lucide-react";
import { useLang } from "../LanguageContext";

const LOGO_PNG = "https://customer-assets.emergentagent.com/job_torre-amat-events/artifacts/5p7voheb_Logo%20Freehand.png";

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);

  const links = [
    { href: "#espacio", label: t.nav.espacio },
    { href: "#eventos", label: t.nav.eventos },
    { href: "#galeria", label: t.nav.galeria },
    { href: "#servicios", label: t.nav.servicios },
    { href: "#testimonios", label: t.nav.testimonios },
    { href: "#contacto", label: t.nav.contacto },
  ];

  const scrollTo = (id) => {
    setOpen(false);
    if (!id || id === "#" || id === "#home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50 bg-[#F5F0E8]/95 backdrop-blur-md border-b border-[#C8A96E]/25"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">

        {/* Logo — PNG, screen blend so black bg disappears, crimson monogram stays */}
        <button
          data-testid="navbar-logo"
          onClick={() => scrollTo("#home")}
          className="flex-shrink-0"
        >
          <img
            src={LOGO_PNG}
            alt="Torre Amat"
            className="h-12 md:h-14 w-auto object-contain"
            style={{ mixBlendMode: "multiply" }}
          />
        </button>

        {/* Desktop links — xl+ */}
        <div className="hidden xl:flex items-center gap-6">
          {links.map((link) => (
            <button
              key={link.href}
              data-testid={`nav-link-${link.href.replace("#", "")}`}
              onClick={() => scrollTo(link.href)}
              className="text-[11px] uppercase tracking-[0.15em] text-[#1A1A18]/70 hover:text-[#C8A96E] transition-colors duration-300 whitespace-nowrap"
              style={{ fontFamily: "Lato, sans-serif" }}
            >
              {link.label}
            </button>
          ))}
        </div>

        {/* Right: lang switcher + CTA */}
        <div className="hidden xl:flex items-center gap-4">
          <div className="flex items-center gap-1">
            {["es", "ca", "en"].map((l, i) => (
              <span key={l} className="flex items-center">
                <button
                  data-testid={`lang-${l}`}
                  onClick={() => setLang(l)}
                  className={`text-[11px] uppercase tracking-widest px-1 py-0.5 transition-all duration-200 ${
                    lang === l
                      ? "text-[#C8A96E] border-b border-[#C8A96E]"
                      : "text-[#1A1A18]/50 hover:text-[#C8A96E]"
                  }`}
                  style={{ fontFamily: "Lato, sans-serif" }}
                >
                  {l.toUpperCase()}
                </button>
                {i < 2 && <span className="text-xs mx-0.5 text-[#1A1A18]/25">/</span>}
              </span>
            ))}
          </div>

          <button
            data-testid="navbar-cta"
            onClick={() => scrollTo("#contacto")}
            className="text-[11px] uppercase tracking-[0.15em] px-4 py-2.5 bg-[#2D4A2D] text-[#F5F0E8] hover:bg-[#C8A96E] hover:text-[#1A1A18] transition-colors duration-300 whitespace-nowrap"
            style={{ fontFamily: "Lato, sans-serif" }}
          >
            {t.nav.solicitar}
          </button>
        </div>

        {/* Hamburger — below xl */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="xl:hidden text-[#1A1A18]"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="xl:hidden bg-[#F5F0E8]/98 backdrop-blur-md border-t border-[#C8A96E]/20 px-6 py-6 space-y-4">
          {links.map((link) => (
            <button
              key={link.href}
              data-testid={`mobile-nav-${link.href.replace("#", "")}`}
              onClick={() => scrollTo(link.href)}
              className="block w-full text-left text-sm uppercase tracking-[0.2em] text-[#1A1A18]/70 hover:text-[#C8A96E] transition-colors py-2 border-b border-[#C8A96E]/15"
              style={{ fontFamily: "Lato, sans-serif" }}
            >
              {link.label}
            </button>
          ))}
          <div className="flex items-center gap-2 pt-2">
            {["es", "ca", "en"].map((l) => (
              <button
                key={l}
                data-testid={`mobile-lang-${l}`}
                onClick={() => { setLang(l); setOpen(false); }}
                className={`text-xs uppercase tracking-widest px-2 py-1 border transition-all ${
                  lang === l
                    ? "border-[#C8A96E] text-[#C8A96E]"
                    : "border-[#1A1A18]/20 text-[#1A1A18]/40 hover:border-[#C8A96E] hover:text-[#C8A96E]"
                }`}
                style={{ fontFamily: "Lato, sans-serif" }}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
          <button
            data-testid="mobile-navbar-cta"
            onClick={() => scrollTo("#contacto")}
            className="block w-full text-center text-xs uppercase tracking-[0.2em] px-5 py-3 bg-[#2D4A2D] text-[#F5F0E8] hover:bg-[#C8A96E] hover:text-[#1A1A18] transition-colors mt-2"
            style={{ fontFamily: "Lato, sans-serif" }}
          >
            {t.nav.solicitar}
          </button>
        </div>
      )}
    </nav>
  );
}
